/** Small DOM hooks for the game screen. */
import { useCallback, useEffect, useLayoutEffect, useState, useSyncExternalStore, type RefObject } from 'react';
import type { ThemePack } from '@dropstack/core';
import type { AudioEngine } from '../contracts.ts';
import { applyThemeCssVars } from '../theme/cssVars.ts';
import { fitFontSize } from './fontFit.ts';

/** Live result of a CSS media query (false where matchMedia is unavailable). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches,
    () => false,
  );
}

/** True once a pen/stylus has touched the page during this run. */
export function usePenUsed(): boolean {
  const [used, setUsed] = useState(false);
  useEffect(() => {
    if (used) return;
    const onPointer = (e: PointerEvent) => {
      if (e.pointerType === 'pen') setUsed(true);
    };
    window.addEventListener('pointerdown', onPointer, { capture: true, passive: true });
    return () => window.removeEventListener('pointerdown', onPointer, { capture: true });
  }, [used]);
  return used;
}

export interface Size {
  width: number;
  height: number;
}

/**
 * An element's size, kept current with a ResizeObserver: the content box
 * (inside padding) or the border box. Layout sizes, so transforms don't count.
 */
export function useElementSize(ref: RefObject<HTMLElement | null>, box: 'content' | 'border' = 'content'): Size {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      let width = el.offsetWidth;
      let height = el.offsetHeight;
      if (box === 'content') {
        const cs = getComputedStyle(el);
        const px = (v: string) => parseFloat(v) || 0;
        width -= px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth);
        height -= px(cs.paddingTop) + px(cs.paddingBottom) + px(cs.borderTopWidth) + px(cs.borderBottomWidth);
      }
      setSize((s) => (Math.abs(s.width - width) < 0.5 && Math.abs(s.height - height) < 0.5 ? s : { width, height }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, box]);
  return size;
}

export interface FitFontOptions {
  /** The (nowrap, inline-block) content to fit; measured at the size currently applied. */
  contentRef: RefObject<HTMLElement | null>;
  availableWidth: number;
  availableHeight?: number;
  basePx: number;
  minPx?: number;
  /** Changes whenever the content changes (e.g. the problem id). */
  contentKey: string;
}

/**
 * Font size (px) at which the content fits: measured before paint, so a new
 * problem never flashes up at the wrong size. Re-fits when the room, the base
 * size or the content changes.
 */
export function useFitFont({ contentRef, availableWidth, availableHeight, basePx, minPx, contentKey }: FitFontOptions): number {
  const [px, setPx] = useState(basePx);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || availableWidth <= 0) return;
    const measuredAtPx = parseFloat(getComputedStyle(el).fontSize) || px;
    const fit = fitFontSize({
      naturalWidth: el.offsetWidth,
      naturalHeight: el.offsetHeight,
      measuredAtPx,
      availableWidth,
      availableHeight,
      basePx,
      minPx,
    });
    if (Math.abs(fit.px - px) >= 0.5) setPx(fit.px);
  }, [contentRef, availableWidth, availableHeight, basePx, minPx, contentKey, px]);
  return px;
}

/** Dress the page in `theme` (CSS variables) and give the music the same theme. */
export function useGameTheme(theme: ThemePack, audio: AudioEngine): void {
  useLayoutEffect(() => applyThemeCssVars(theme), [theme]);
  useEffect(() => audio.setTheme(theme), [audio, theme]);
}
